import { HttpInterceptorFn, HttpErrorResponse, HttpHandlerFn, HttpRequest, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError, Observable, of, map, finalize, timeout, from } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../../shared/services/toast.service';
import { StorageService } from '../services/storage.service';

// ── Refresh deduplication state ────────────────────────────────

interface RefreshState {
  isRefreshing: boolean;
  refreshPromise: Observable<string> | null;
}

const refreshState: RefreshState = {
  isRefreshing: false,
  refreshPromise: null,
};

// ── Excluded URLs (no auth header, no 401 handling) ────────────

const EXCLUDED_PATTERNS = [
  /\/auth\/login$/,
  /\/auth\/register$/,
  /\/auth\/sign-up$/,
  /\/auth\/signup$/,
  /\/auth\/firebase/,
  /\/auth\/google/,
  /\/auth\/apple/,
  /\/auth\/forgot-password$/,
  /\/auth\/reset-password$/,
  /\/auth\/request-otp$/,
  /\/auth\/verify-otp$/,
  /\/auth\/validate-reset-token$/,
  /\/auth\/verify-email$/,
  /\/auth\/complete-firebase-password-reset$/,
  /\/auth\/refresh$/,
  /\/health$/,
  /\/system\/health$/,
];

function isExcluded(url: string): boolean {
  return EXCLUDED_PATTERNS.some((p) => p.test(url));
}

// ── Token helpers ──────────────────────────────────────────────

function getTokenExpiryMs(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function addTokenHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
  return request.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
  });
}

// ── Interceptor ────────────────────────────────────────────────

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const storage = inject(StorageService);

  // Public endpoints — pass through untouched
  if (isExcluded(req.url)) {
    return next(req);
  }

  // Logout may return 404 legitimately — don't trigger refresh
  if (req.url.includes('/auth/logout')) {
    return next(req).pipe(
      catchError(() => of({} as HttpEvent<any>)),
    );
  }

  // Wait for IndexedDB cache to load before reading the token, otherwise the
  // first request on page load always sees null and triggers a spurious 401.
  return from(storage.ready()).pipe(
    switchMap(() => authInterceptorCore(req, next, authService)),
  );
};

function authInterceptorCore(
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
): Observable<HttpEvent<any>> {
  const token = authService.getToken();

  // ── Proactive refresh: refresh if token expires within 5 min ──
  if (token) {
    const expiryMs = getTokenExpiryMs(token);
    const FIVE_MINUTES = 5 * 60 * 1000;

    if (expiryMs) {
      const timeUntilExpiry = expiryMs - Date.now();

      if (timeUntilExpiry < FIVE_MINUTES && timeUntilExpiry > 0) {
        return refreshAndRetry(authService).pipe(
          switchMap((refreshedToken) =>
            next(addTokenHeader(req, refreshedToken)).pipe(
              timeout(60000),
            )
          ),
          catchError(() =>
            // Proactive refresh failed — try with current token
            next(addTokenHeader(req, token)).pipe(
              timeout(60000),
              catchError((error) => handleRequestError(error, req, next, authService)),
            )
          ),
        );
      }
    }
  }

  // ── Normal flow: attach token if available ───────────────────
  if (token) {
    req = addTokenHeader(req, token);
  }

  return next(req).pipe(
    timeout(60000),
    catchError((error) => handleRequestError(error, req, next, authService)),
  );
}

// ── Error handler ──────────────────────────────────────────────

function handleRequestError(
  error: any,
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
): Observable<HttpEvent<any>> {
  if (!(error instanceof HttpErrorResponse)) {
    return throwError(() => error);
  }

  // Only handle 401 for non-excluded URLs
  if (error.status !== 401 || isExcluded(error.url || '')) {
    return throwError(() => error);
  }

  // Try to refresh and retry once
  return refreshAndRetry(authService).pipe(
    switchMap((refreshedToken) =>
      next(addTokenHeader(req, refreshedToken)).pipe(
        timeout(60000),
      )
    ),
    catchError((refreshError) => {
      // Only treat as a real session expiry if the refresh endpoint itself returned 401/403.
      // "No refresh token" means the backend doesn't issue them — the existing access token
      // may still be valid, so don't nuke the session or show a misleading toast.
      const isRealExpiry = refreshError instanceof HttpErrorResponse
        && (refreshError.status === 401 || refreshError.status === 403);

      if (isRealExpiry) {
        authService.clearToken();
        try {
          const toast = inject(ToastService);
          toast.showError('Your session has expired. Please log in again.', { duration: 6000 });
        } catch {
          console.warn('[Auth] Could not show session-expired toast');
        }
      }

      return throwError(() => refreshError);
    }),
  );
}

// ── Refresh with deduplication ─────────────────────────────────

function refreshAndRetry(authService: AuthService): Observable<string> {
  if (refreshState.isRefreshing && refreshState.refreshPromise) {
    return refreshState.refreshPromise;
  }

  refreshState.isRefreshing = true;
  refreshState.refreshPromise = authService.refreshToken().pipe(
    map(() => {
      const newToken = authService.getTokenSync();
      if (!newToken) {
        throw new Error('Token refresh did not produce an access token');
      }
      return newToken;
    }),
    finalize(() => {
      refreshState.isRefreshing = false;
      refreshState.refreshPromise = null;
    }),
  );

  return refreshState.refreshPromise;
}
